import { isDefined } from "@prismicio/editor-support/Type"
import type { ImageContent } from "@prismicio/types-internal/lib/content"
import type { Image } from "@prismicio/types-internal/lib/customtypes"
import { Suspense, useState } from "react"

import { getImageLabel } from "../../core/imageUtils"
import { getFieldLabel } from "../utils"
import { EmptyCardContent } from "./Components/EmptyCardContent"
import { ImageFieldCard } from "./Components/ImageFieldCard"
import { ImageFieldDropZone } from "./Components/ImageFieldDropZone"
import { NonEmptyCardContent } from "./Components/NonEmptyCardContent"
import { useImageField } from "./useImageField"

export interface ImageFieldProps {
  content: ImageContent | undefined
  field: Image
  readOnly: boolean
  onContentChange: (content: ImageContent | undefined) => void
}

export function ImageField(props: ImageFieldProps) {
  const { content, field, readOnly, onContentChange } = props

  /**
   * Variable detailing if origin url has any data.
   * Edge case for default legacy slices as their origin url might be empty
   */
  const hasOriginUrl = content && content.origin.url.length > 0

  const {
    onCropperDialogClose,
    onPageChange,
    isUpdating,
    page,
    onMediaSelected,
    thumbnail,
    allThumbnails,
    allContentViews,
    contentView,
    onClear,
    uploadImage,
    isUploadingImage,
  } = useImageField({
    field,
    content,
    onContentChange,
  })

  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false)

  const fieldLabel = getFieldLabel(field)

  const images = allThumbnails.map((thumbnail, index) => {
    const contentView = allContentViews?.[index]
    return {
      thumbnail,
      contentView,
      labels: getImageLabel(thumbnail, content, contentView),
    }
  })

  const imageLabel =
    thumbnail && hasOriginUrl
      ? getImageLabel(thumbnail, content, contentView).filter(isDefined)
      : []

  const showCardContent =
    thumbnail && content && hasOriginUrl && allContentViews

  const isFieldUpdating = isUploadingImage || isUpdating

  return (
    <ImageFieldCard
      isMediaDialogOpen={isMediaDialogOpen}
      onMediaSelected={onMediaSelected}
      onMediaDialogOpenChange={setIsMediaDialogOpen}
    >
      <Suspense fallback={<div style={{ height: 95 }} />}>
        {showCardContent ? (
          <NonEmptyCardContent
            allContentViews={allContentViews}
            content={content}
            contentView={contentView}
            fieldLabel={fieldLabel}
            imageLabel={imageLabel}
            initialImages={images}
            isUpdating={isFieldUpdating}
            page={page}
            readOnly={readOnly}
            thumbnailName={thumbnail.name}
            onClear={onClear}
            onContentChange={onContentChange}
            onCropperDialogClose={onCropperDialogClose}
            onMediaDialogOpenChange={setIsMediaDialogOpen}
            onPageChange={onPageChange}
          />
        ) : (
          <ImageFieldDropZone
            uploadImage={uploadImage}
            isUploading={isUploadingImage}
            disabled={readOnly || isMediaDialogOpen}
          >
            <EmptyCardContent
              fieldLabel={fieldLabel}
              isUpdating={isFieldUpdating}
              readOnly={readOnly}
              onMediaDialogOpenChange={setIsMediaDialogOpen}
            />
          </ImageFieldDropZone>
        )}
      </Suspense>
    </ImageFieldCard>
  )
}
